import { useEffect, useRef, useState } from 'react';
import { CalendarDays, ClipboardList, Clock3, Timer, UserRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authService, trainingsService } from '../services/api';
import { formatDate, formatTime, getAttachmentIcon } from './utils/Trainings'
import './css/Trainings.css'

const AttachmentIcon = ({ type, size = 22 }) => {
  const Icon = getAttachmentIcon(type);
  return <Icon size={size} strokeWidth={1.8} aria-hidden="true" />;
};

const Trainings = () => {
  const { hasPermission } = useAuth();
  const [trainings, setTrainings] = useState({ upcoming: [], past: [] });
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [logos, setLogos] = useState([]);
  const [showLogoGallery, setShowLogoGallery] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [attachmentName, setAttachmentName] = useState('');
  const [showAttachmentNameModal, setShowAttachmentNameModal] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [urlAttachment, setUrlAttachment] = useState({ name: '', url: '', type: 'video' });
  const fileInputRef = useRef(null);
  
  const canManageTrainings = hasPermission('trainings:manage');
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    departments: [],  // Changed from department to departments array
    training_date: '',
    training_time: '',
    duration_minutes: '',
    logo_url: '',
    external_link: '',
    recording_url: '',
    attachments: [],
    is_published: true
  });

  useEffect(() => {
    fetchTrainings();
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (showForm && canManageTrainings) {
      fetchLogos();
    }
  }, [showForm, canManageTrainings]);

  const fetchDepartments = async () => {
    try {
      const data = await authService.getDepartments();
      setDepartments(data || []);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setDepartments([]);
    }
  };

  const fetchTrainings = async () => {
    try {
      setLoading(true);
      const data = await trainingsService.getAllTrainings();
      setTrainings(data);
    } catch (err) {
      console.error('Error fetching trainings:', err);
      setError('Failed to load trainings');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogos = async () => {
    try {
      const data = await trainingsService.getLogos();
      setLogos(data.logos || []);
    } catch (err) {
      console.error('Error fetching logos:', err);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      departments: [],
      training_date: '',
      training_time: '',
      duration_minutes: '',
      logo_url: '',
      external_link: '',
      recording_url: '',
      attachments: [],
      is_published: true
    });
    setEditingTraining(null);
    setShowForm(false);
    setShowLogoGallery(false);
    setPendingFile(null);
    setAttachmentName('');
    setShowAttachmentNameModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    try {
      // Combine date and time
      const dateTime = new Date(`${form.training_date}T${form.training_time || '00:00'}`);
      
      const payload = {
        title: form.title,
        description: form.description || null,
        departments: form.departments.length > 0 ? form.departments : null,
        training_date: dateTime.toISOString(),
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null,
        logo_url: form.logo_url || null,
        external_link: form.external_link || null,
        recording_url: form.recording_url || null,
        attachments: form.attachments.length > 0 ? form.attachments : null,
        is_published: form.is_published
      };
      
      if (editingTraining) {
        await trainingsService.updateTraining(editingTraining.id, payload);
        showSuccess('Training updated successfully');
      } else {
        await trainingsService.createTraining(payload);
        showSuccess('Training created successfully');
      }
      
      resetForm();
      fetchTrainings();
    } catch (err) {
      console.error('Error saving training:', err);
      setError(err.response?.data?.detail || 'Failed to save training');
    }
  };

  const handleEdit = (training) => {
    const date = new Date(training.training_date);
    setForm({
      title: training.title,
      description: training.description || '',
      departments: training.departments || [],
      training_date: date.toISOString().split('T')[0],
      training_time: date.toTimeString().slice(0, 5),
      duration_minutes: training.duration_minutes || '',
      logo_url: training.logo_url || '',
      external_link: training.external_link || '',
      recording_url: training.recording_url || '',
      attachments: training.attachments || [],
      is_published: training.is_published
    });
    setEditingTraining(training);
    setShowForm(true);
  };

  const handleDelete = async (trainingId) => {
    if (!window.confirm('Are you sure you want to delete this training?')) return;
    
    try {
      await trainingsService.deleteTraining(trainingId);
      showSuccess('Training deleted successfully');
      fetchTrainings();
    } catch (err) {
      console.error('Error deleting training:', err);
      setError('Failed to delete training');
    }
  };

  const handleCancel = async (trainingId) => {
    if (!window.confirm('Are you sure you want to cancel this training?')) return;
    
    try {
      await trainingsService.cancelTraining(trainingId);
      showSuccess('Training cancelled');
      fetchTrainings();
    } catch (err) {
      console.error('Error cancelling training:', err);
      setError('Failed to cancel training');
    }
  };

  const addAttachment = () => {
    setUrlAttachment({ name: '', url: '', type: 'video' });
    setShowUrlModal(true);
  };

  const handleUrlAttachmentConfirm = () => {
    if (!urlAttachment.name.trim() || !urlAttachment.url.trim()) {
      setError('Please enter both a name and URL');
      return;
    }
    
    setForm(prev => ({
      ...prev,
      attachments: [...prev.attachments, { 
        name: urlAttachment.name.trim(), 
        url: urlAttachment.url.trim(), 
        type: urlAttachment.type 
      }]
    }));
    setShowUrlModal(false);
    setUrlAttachment({ name: '', url: '', type: 'video' });
    showSuccess('Link added successfully');
  };

  const handleUrlAttachmentCancel = () => {
    setShowUrlModal(false);
    setUrlAttachment({ name: '', url: '', type: 'video' });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!validTypes.includes(file.type)) {
      setError('Please upload a valid document file (PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX)');
      event.target.value = '';
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      setError('File too large. Maximum size is 50MB');
      event.target.value = '';
      return;
    }

    // Store the file and show the name modal
    setPendingFile(file);
    // Set default name to file name without extension
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
    setAttachmentName(nameWithoutExt);
    setShowAttachmentNameModal(true);
  };

  const handleAttachmentNameConfirm = async () => {
    if (!pendingFile || !attachmentName.trim()) {
      setError('Please enter a name for the attachment');
      return;
    }

    try {
      setUploadingAttachment(true);
      setError(null);
      setShowAttachmentNameModal(false);
      
      const result = await trainingsService.uploadAttachment(pendingFile);
      const attachmentUrl = trainingsService.getAttachmentUrl(result.filename);
      
      // Determine type based on extension
      const ext = pendingFile.name.split('.').pop().toLowerCase();
      const extToTypeMap = {
          pdf: "pdf",
          ppt: "presentation",
          pptx: "presentation",
          doc: "file",
          docx: "file",
          xls: "file",
          xlsx: "file",
      };
      let type = extToTypeMap[ext] || "file";
      
      setForm(prev => ({
        ...prev,
        attachments: [...prev.attachments, { 
          name: attachmentName.trim(), 
          url: attachmentUrl, 
          type 
        }]
      }));
      showSuccess('Attachment uploaded successfully');
    } catch (err) {
      console.error('Error uploading attachment:', err);
      setError(err.response?.data?.detail || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
      setPendingFile(null);
      setAttachmentName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAttachmentNameCancel = () => {
    setShowAttachmentNameModal(false);
    setPendingFile(null);
    setAttachmentName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      setError('File too large. Maximum size is 50MB');
      return;
    }

    try {
      setUploadingLogo(true);
      setError(null);
      const result = await trainingsService.uploadLogo(file);
      const logoUrl = trainingsService.getLogoUrl(result.filename);
      setForm(prev => ({ ...prev, logo_url: logoUrl }));
      showSuccess('Logo uploaded successfully');
      fetchLogos();
      setShowLogoGallery(false);
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const selectLogo = (logo) => {
    const logoUrl = trainingsService.getLogoUrl(logo.filename);
    setForm(prev => ({ ...prev, logo_url: logoUrl }));
    setShowLogoGallery(false);
  };

  const clearLogo = () => {
    setForm(prev => ({ ...prev, logo_url: '' }));
  };

  const removeAttachment = (index) => {
    setForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const TrainingCard = ({ training, isPast }) => (
    <div className={`training-card ${isPast ? 'past' : 'upcoming'} ${training.is_cancelled ? 'cancelled' : ''}`}>
      <div className="card-content-wrapper">
        {/* Left Section: Logo */}
        {training.logo_url && (
          <div className="training-logo-container">
            <img src={training.logo_url} alt={training.title} className="training-logo-img" />
          </div>
        )}
        
        {/* Right Section: Main Content */}
        <div className="training-main-content">
          {/* Header Row */}
          <div className="training-header-row">
            <div className="training-title-section">
              <h3 className="training-title">{training.title}</h3>
              {training.departments && training.departments.length > 0 && (
                <div className="departments-tags">
                  {training.departments.map((dept, idx) => (
                    <span key={idx} className="dept-tag">
                      🏢 {dept}
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {canManageTrainings && (
              <div className="training-actions">
                <button className="action-btn edit-btn" onClick={() => handleEdit(training)} title="Edit">
                  <span className="btn-icon">✏️</span>
                  <span className="btn-text">Edit</span>
                </button>
                {!isPast && !training.is_cancelled && (
                  <button className="action-btn cancel-btn" onClick={() => handleCancel(training.id)} title="Cancel">
                    <span className="btn-icon">🚫</span>
                    <span className="btn-text">Cancel</span>
                  </button>
                )}
                <button className="action-btn delete-btn" onClick={() => handleDelete(training.id)} title="Delete">
                  <span className="btn-icon">🗑️</span>
                  <span className="btn-text">Delete</span>
                </button>
              </div>
            )}
          </div>

          {/* Meta Information Grid */}
          <div className="training-meta-grid">
            <div className="meta-item">
              <span className="meta-icon"><CalendarDays aria-hidden="true" /></span>
              <div className="meta-content">
                <span className="meta-label">Data</span>
                <span className="meta-value">{formatDate(training.training_date)}</span>
              </div>
            </div>
            <div className="meta-item">
              <span className="meta-icon"><Clock3 aria-hidden="true" /></span>
              <div className="meta-content">
                <span className="meta-label">Godzina</span>
                <span className="meta-value">{formatTime(training.training_date)}</span>
              </div>
            </div>
            {training.duration_minutes && (
              <div className="meta-item">
                <span className="meta-icon"><Timer aria-hidden="true" /></span>
                <div className="meta-content">
                  <span className="meta-label">Czas trwania</span>
                  <span className="meta-value">{training.duration_minutes} minut</span>
                </div>
              </div>
            )}
            <div className="meta-item">
              <span className="meta-icon"><UserRound aria-hidden="true" /></span>
              <div className="meta-content">
                <span className="meta-label">Utworzone przez</span>
                <span className="meta-value">{training.author_name}</span>
              </div>
            </div>
          </div>

          {/* Description */}
          {training.description && (
            <div className="training-description-section">
              <h4 className="description-heading"><ClipboardList aria-hidden="true" /> O szkoleniu</h4>
              <p className="training-description">{training.description}</p>
            </div>
          )}

          {/* Action Buttons and Resources */}
          <div className="training-resources">
            {/* Primary Action Links */}
            {(training.external_link || training.recording_url) && (
              <div className="primary-links">
                {training.external_link && (
                  <a 
                    href={training.external_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="primary-link meeting-link"
                  >
                    <span className="link-icon">🔗</span>
                    <span className="link-text">Dołącz do spotkania</span>
                    <span className="link-arrow">→</span>
                  </a>
                )}
                {training.recording_url && (
                  <a 
                    href={training.recording_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="primary-link recording-link"
                  >
                    <span className="link-icon">🎬</span>
                    <span className="link-text">Oglądaj nagranie</span>
                    <span className="link-arrow">→</span>
                  </a>
                )}
              </div>
            )}
            
            {/* Attachments */}
            {training.attachments && training.attachments.length > 0 && (
              <div className="attachments-section">
                <h4 className="attachments-heading">📎 Materiały szkoleniowe</h4>
                <div className="attachments-list">
                  {training.attachments.map((att, idx) => (
                    <a 
                      key={idx}
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="attachment-item"
                    >
                      <span className="attachment-icon"><AttachmentIcon type={att.type} /></span>
                      <span className="attachment-name">{att.name}</span>
                      <span className="attachment-download">↓</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page-container trainings-page">
      <div className="page-header">
        <h2>Szkolenia firmowe</h2>
      </div>

      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}

      {/* Add Training Form - Only for users with permission */}
      {canManageTrainings && (
        <div className="trainings-admin-section">
          {showForm && (
            <div className="training-form-container">
              <h3>{editingTraining ? 'Edytuj szkolenie' : 'Dodaj nowe szkolenie'}</h3>
              <form onSubmit={handleSubmit} className="training-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Tytuł szkolenia *</label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm({...form, title: e.target.value})}
                      placeholder="np. Szkolenie BHP 2026"
                      required
                    />
                  </div>
                </div>

                {/* Multi-select Departments */}
                <div className="form-group">
                  <label>Docelowe działy <span className="hint">(Pozostaw puste dla wszystkich działów)</span></label>
                  <div className="departments-multiselect">
                    {departments.map(dept => (
                      <label key={dept} className={`department-checkbox ${form.departments.includes(dept) ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={form.departments.includes(dept)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setForm({...form, departments: [...form.departments, dept]});
                            } else {
                              setForm({...form, departments: form.departments.filter(d => d !== dept)});
                            }
                          }}
                        />
                        {dept}
                      </label>
                    ))}
                  </div>
                  {form.departments.length > 0 && (
                    <div className="selected-departments">
                      <span>Wybrane: {form.departments.join(', ')}</span>
                      <button type="button" className="btn-clear-depts" onClick={() => setForm({...form, departments: []})}>
                        Wyczyść wszystkie
                      </button>
                    </div>
                  )}
                </div>

                {/* Logo Selection Section */}
                <div className="form-group logo-section">
                  <label>Logo/Obraz szkolenia/ firmy prowadzącej</label>
                  
                  {/* Current logo preview */}
                  {form.logo_url && (
                    <div className="current-logo-preview">
                      <img src={form.logo_url} alt="Selected logo" />
                      <button type="button" className="btn-remove-logo" onClick={clearLogo}>✕ Usuń</button>
                    </div>
                  )}
                  
                  {/* Logo options */}
                  <div className="logo-options">
                    <button 
                      type="button" 
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowLogoGallery(!showLogoGallery)}
                    >
                      {showLogoGallery ? '📁 Ukryj galerię' : '📁 Wybierz z galerii'}
                    </button>
                    
                    <label className="btn btn-secondary btn-sm upload-btn">
                      {uploadingLogo ? '⏳ Przesyłanie...' : '📤 Prześlij nowe logo'}
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleLogoUpload}
                        disabled={uploadingLogo}
                        hidden
                      />
                    </label>
                  </div>
                  
                  {/* Logo Gallery */}
                  {showLogoGallery && (
                    <div className="logo-gallery">
                      {logos.length > 0 ? (
                        <div className="logo-grid">
                          {logos.map((logo, idx) => (
                            <div 
                              key={idx} 
                              className={`logo-item ${form.logo_url.includes(logo.filename) ? 'selected' : ''}`}
                              onClick={() => selectLogo(logo)}
                            >
                              <img src={trainingsService.getLogoUrl(logo.filename)} alt={logo.filename} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-logos">Brak przesłanych logo. Prześlij swoje pierwsze logo powyżej.</p>
                      )}
                    </div>
                  )}
                  
                  {/* Manual URL input */}
                  <div className="manual-url-input">
                    <label>Lub wprowadź URL ręcznie:</label>
                    <input
                      type="url"
                      value={form.logo_url}
                      onChange={(e) => setForm({...form, logo_url: e.target.value})}
                      placeholder="https://example.com/logo.jpg"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Data *</label>
                    <input
                      type="date"
                      value={form.training_date}
                      onChange={(e) => setForm({...form, training_date: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Godzina</label>
                    <input
                      type="time"
                      value={form.training_time}
                      onChange={(e) => setForm({...form, training_time: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Czas trwania (minuty)</label>
                    <input
                      type="number"
                      value={form.duration_minutes}
                      onChange={(e) => setForm({...form, duration_minutes: e.target.value})}
                      placeholder="60"
                      min="1"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Opis / Notatki</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({...form, description: e.target.value})}
                    placeholder="Opis szkolenia, agenda, wymagania..."
                    rows="4"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Link do spotkania (Teams/Zoom)</label>
                    <input
                      type="url"
                      value={form.external_link}
                      onChange={(e) => setForm({...form, external_link: e.target.value})}
                      placeholder="https://teams.microsoft.com/..."
                    />
                  </div>
                  <div className="form-group">
                    <label>Link do nagrania (po szkoleniu)</label>
                    <input
                      type="url"
                      value={form.recording_url}
                      onChange={(e) => setForm({...form, recording_url: e.target.value})}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Załączniki (PDF, DOC, PPT, XLS)</label>
                  <div className="attachments-editor">
                    {form.attachments.map((att, idx) => (
                      <div key={idx} className="attachment-item">
                        <span className="attachment-editor-name"><AttachmentIcon type={att.type} size={18} /> {att.name}</span>
                        <button type="button" onClick={() => removeAttachment(idx)}>✕</button>
                      </div>
                    ))}
                    <div className="attachment-buttons">
                      <label className="btn btn-secondary btn-sm upload-btn">
                        {uploadingAttachment ? '⏳ Przesyłanie...' : '📤 Prześlij plik'}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                          onChange={handleFileSelect}
                          disabled={uploadingAttachment}
                          hidden
                        />
                      </label>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={addAttachment}>
                        🔗 Dodaj URL
                      </button>
                    </div>
                  </div>
                </div>

                {/* Attachment Name Modal */}
                {showAttachmentNameModal && (
                  <div className="modal-overlay">
                    <div className="modal-content">
                      <h4>Nazwij swój załącznik</h4>
                      <p className="modal-hint">Wprowadź nazwę wyświetlaną dla tego pliku (np. "Katalog Produktów", "Podręcznik Szkoleniowy")</p>
                      <input
                        type="text"
                        value={attachmentName}
                        onChange={(e) => setAttachmentName(e.target.value)}
                        placeholder="Wprowadź nazwę załącznika..."
                        autoFocus
                      />
                      <div className="modal-actions">
                        <button type="button" className="btn btn-primary" onClick={handleAttachmentNameConfirm}>
                          Prześlij
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={handleAttachmentNameCancel}>
                          Anuluj
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* URL Link Modal */}
                {showUrlModal && (
                  <div className="modal-overlay">
                    <div className="modal-content url-modal">
                      <h4>Dodaj link</h4>
                      <p className="modal-hint">Dodaj link do filmu na YouTube lub innego zasobu</p>
                      
                      <div className="modal-field">
                        <label>Nazwa wyświetlana *</label>
                        <input
                          type="text"
                          value={urlAttachment.name}
                          onChange={(e) => setUrlAttachment(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="np. Film szkoleniowy, Prezentacja produktu..."
                          autoFocus
                        />
                      </div>
                      
                      <div className="modal-field">
                        <label>URL *</label>
                        <input
                          type="url"
                          value={urlAttachment.url}
                          onChange={(e) => setUrlAttachment(prev => ({ ...prev, url: e.target.value }))}
                          placeholder="https://youtube.com/watch?v=... lub dowolny URL"
                        />
                      </div>
                      
                      <div className="modal-field">
                        <label>Type</label>
                        <select
                          value={urlAttachment.type}
                          onChange={(e) => setUrlAttachment(prev => ({ ...prev, type: e.target.value }))}
                        >
                          <option value="video">🎥 Video</option>
                          <option value="pdf">📄 PDF/Document</option>
                          <option value="presentation">📊 Presentation</option>
                          <option value="file">📎 Other</option>
                        </select>
                      </div>
                      
                      <div className="modal-actions">
                        <button type="button" className="btn btn-primary" onClick={handleUrlAttachmentConfirm}>
                          Dodaj link
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={handleUrlAttachmentCancel}>
                          Anuluj
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={form.is_published}
                      onChange={(e) => setForm({...form, is_published: e.target.checked})}
                    />
                    Opublikuj natychmiast (jeśli nie zaznaczone, szkolenie będzie niewidoczne dla pracowników do momentu publikacji)
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingTraining ? 'Aktualizuj szkolenie' : 'Utwórz szkolenie'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Anuluj
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading-spinner">Ładowanie szkoleń...</div>
      ) : (
        <div className="trainings-content">
          {/* Upcoming Trainings Section */}
          <section className="trainings-section upcoming-section">
            <div className="section-header">
              <h3>Nadchodzące szkolenia</h3>
              <span className="section-count">{trainings.upcoming.length} zaplanowane</span>
            </div>
            
            {trainings.upcoming.length > 0 ? (
              <div className="trainings-grid">
                {trainings.upcoming.map(training => (
                  <TrainingCard key={training.id} training={training} isPast={false} />
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon"><CalendarDays aria-hidden="true" /></div>
                <h4>Brak nadchodzących szkoleń</h4>
                <p>Obecnie nie ma zaplanowanych szkoleń.</p>
                {canManageTrainings && (
                  <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                    Zaplanuj szkolenie
                  </button>
                )}
              </div>
            )}
          </section>

          {/* Training Archive Section */}
          <section className="trainings-section archive-section">
            <div className="section-header">
              <h3>Archiwum szkoleń</h3>
              <span className="section-count">{trainings.past.length} zakończone</span>
            </div>
            
            {trainings.past.length > 0 ? (
              <div className="trainings-grid archive">
                {trainings.past.map(training => (
                  <TrainingCard key={training.id} training={training} isPast={true} />
                ))}
              </div>
            ) : (
              <div className="empty-state small">
                <p>Brak szkoleń w archiwum.</p>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default Trainings;
